/**
 * SignalingClient — Simple WebSocket wrapper for signaling.
 * Matches the reference implementation exactly: no auto-reconnect,
 * no heartbeat, no message queue. Just connect, send, receive.
 */

export interface SignalingMessage {
  type: string;
  payload: unknown;
}

export interface SignalingCallbacks {
  onMessage: (msg: SignalingMessage) => void;
  onOpen: () => void;
  onClose: (code: number, reason: string) => void;
  onError: (error: Event) => void;
}

export class SignalingClient {
  private ws: WebSocket | null = null;
  private callbacks: SignalingCallbacks;

  constructor(callbacks: SignalingCallbacks) {
    this.callbacks = callbacks;
  }

  connect(signalingUrl: string, roomId: string, secret: string): void {
    const url = `${signalingUrl}?roomId=${encodeURIComponent(roomId)}&secret=${encodeURIComponent(secret)}`;
    console.log(`[signaling] Connecting to ${url}`);

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log('[signaling] Connected');
      this.callbacks.onOpen();
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as SignalingMessage;
        console.log(`[signaling] Received: ${msg.type}`);
        this.callbacks.onMessage(msg);
      } catch (err) {
        console.error('[signaling] Failed to parse message:', err);
      }
    };

    this.ws.onclose = (event) => {
      console.log(`[signaling] Closed: ${event.code} ${event.reason}`);
      this.callbacks.onClose(event.code, event.reason);
    };

    this.ws.onerror = (event) => {
      console.error('[signaling] Error:', event);
      this.callbacks.onError(event);
    };
  }

  send(msg: SignalingMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      console.log(`[signaling] Sent: ${msg.type}`);
    } else {
      console.warn('[signaling] Cannot send — WebSocket not open');
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
